/**
 * GenderBreakdownChart
 *
 * Modo normal (sem previousData):
 *   Pie chart + totais absolutos.
 *
 * Modo comparativo YoY (com previousData):
 *   Pie chart (proporção atual) + grouped BarChart (atual vs anterior por gênero)
 *   + cards de delta com seta.
 *
 * Zero `any`.
 */

import React from 'react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
} from 'recharts';
import type { PieLabelRenderProps, TooltipContentProps } from 'recharts';
import { GenderStats } from '../../domain/entities/Analytics';
import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';

// ─── Interfaces ────────────────────────────────────────────────────────────────

export interface GenderBreakdownChartProps {
  data: GenderStats;
  previousData?: GenderStats;
}

interface GenderChartPoint {
  name: string;
  value: number;
  percentage: number;
  [key: string]: string | number;
}

interface BarComparePoint {
  gender: string;
  Atual: number;
  Anterior: number;
  [key: string]: string | number;
}

interface GenderYoYDeltaProps {
  label: string;
  color: string;
  diff: number;
  pct: number | null;
  prev: number;
  cur: number;
}

// ─── Constantes ────────────────────────────────────────────────────────────────

const GENDER_COLORS = ['#3B82F6', '#EC4899'] as const;

const BAR_ATUAL    = '#6366f1'; // índigo — neutro, representa "agora"
const BAR_ANTERIOR = '#94a3b8'; // cinza azulado — representa "antes"

// ─── Helpers ───────────────────────────────────────────────────────────────────

const renderPieLabel = (props: PieLabelRenderProps): React.ReactElement | null => {
  const RADIAN = Math.PI / 180;
  const cx        = Number(props.cx        ?? 0);
  const cy        = Number(props.cy        ?? 0);
  const midAngle  = Number(props.midAngle  ?? 0);
  const innerR    = Number(props.innerRadius ?? 0);
  const outerR    = Number(props.outerRadius ?? 0);
  const percent   = Number(props.percent   ?? 0);
  if (percent < 0.03) return null; // fatia muito pequena — pula
  const r = innerR + (outerR - innerR) * 0.55;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central"
      fontSize={13} fontWeight="bold" style={{ pointerEvents: 'none' }}>
      {`${(percent * 100).toFixed(1)}%`}
    </text>
  );
};

/** Tooltip do BarChart de comparação */
const BarCompareTooltip = (
  { active, payload, label }: TooltipContentProps<number, string>,
): React.ReactElement | null => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 text-sm">
      <p className="font-semibold text-gray-900 dark:text-white mb-2">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-medium">
          {p.name}: {Number(p.value).toLocaleString('pt-BR')} pessoas
        </p>
      ))}
    </div>
  );
};

// ─── Sub-componente: card de delta YoY ─────────────────────────────────────────

const GenderYoYDelta: React.FC<GenderYoYDeltaProps> = ({ label, color, diff, pct, prev, cur }) => {
  const positive = diff > 0;
  const neutral  = diff === 0;
  const Icon  = neutral ? Minus : positive ? ArrowUpRight : ArrowDownRight;
  const badge = neutral
    ? 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
    : positive
      ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
      : 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400';

  return (
    <div className="flex flex-col items-center gap-1.5">
      {/* Identificador do gênero */}
      <div className="flex items-center gap-1.5">
        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
        <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">{label}</span>
      </div>

      {/* Badge de variação */}
      <span className={`inline-flex items-center gap-1 font-bold text-lg px-2.5 py-1 rounded-lg ${badge}`}>
        <Icon className="w-4 h-4" />
        {pct !== null
          ? `${positive ? '+' : ''}${pct.toFixed(1).replace('.', ',')}%`
          : '—'}
      </span>

      {/* Números absolutos */}
      <p className="text-xs text-gray-400 dark:text-gray-500">
        {prev.toLocaleString('pt-BR')} → <span className="font-semibold text-gray-700 dark:text-gray-200">{cur.toLocaleString('pt-BR')}</span>
      </p>
    </div>
  );
};

// ─── Componente principal ──────────────────────────────────────────────────────

export const GenderBreakdownChart: React.FC<GenderBreakdownChartProps> = ({
  data,
  previousData,
}) => {
  const chartData: GenderChartPoint[] = [
    { name: 'Homens',   value: data.male,   percentage: data.male_percentage },
    { name: 'Mulheres', value: data.female, percentage: data.female_percentage },
  ];

  // Dados para o BarChart comparativo (só calculado quando previousData existe)
  const barData: BarComparePoint[] = previousData
    ? [
        { gender: 'Homens',   Atual: data.male,   Anterior: previousData.male   },
        { gender: 'Mulheres', Atual: data.female, Anterior: previousData.female },
      ]
    : [];

  const maleDiff   = previousData ? data.male   - previousData.male   : 0;
  const femaleDiff = previousData ? data.female - previousData.female : 0;
  const malePct    = previousData && previousData.male   > 0 ? (maleDiff   / previousData.male)   * 100 : null;
  const femalePct  = previousData && previousData.female > 0 ? (femaleDiff / previousData.female) * 100 : null;

  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
      <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-100">
        Distribuição por Gênero
      </h3>

      {/* Layout: pie + (barras se comparativo) */}
      <div className={`grid gap-6 ${previousData ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>

        {/* ── Pie chart — proporção do período atual ── */}
        <div>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderPieLabel}
                outerRadius={95}
                dataKey="value"
              >
                {chartData.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={GENDER_COLORS[index]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number, name: string, item: { payload?: unknown }) => {
                  const point = item.payload as GenderChartPoint;
                  return [
                    `${value.toLocaleString('pt-BR')} pessoas (${point?.percentage?.toFixed(1) ?? '0'}%)`,
                    name,
                  ] as [string, string];
                }}
                contentStyle={{
                  backgroundColor: 'rgba(255,255,255,0.95)',
                  border: '1px solid #ccc',
                  borderRadius: '8px',
                }}
                wrapperClassName="dark:text-gray-900"
              />
            </PieChart>
          </ResponsiveContainer>

          {/* Legenda manual (mantém no dark mode) */}
          <div className="flex justify-center gap-5 mt-1">
            {chartData.map((d, i) => (
              <div key={d.name} className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: GENDER_COLORS[i] }} />
                {d.name}
              </div>
            ))}
          </div>
          {previousData && (
            <p className="text-xs text-center text-gray-400 dark:text-gray-500 mt-2 uppercase tracking-wide">
              Proporção atual
            </p>
          )}

          {/* Totais absolutos */}
          <div className="mt-4 grid grid-cols-2 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {data.male.toLocaleString('pt-BR')}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Homens</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-pink-600 dark:text-pink-400">
                {data.female.toLocaleString('pt-BR')}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Mulheres</p>
            </div>
          </div>
        </div>

        {/* ── Comparativo YoY — só aparece quando há previousData ── */}
        {previousData && (
          <div>
            <p className="text-xs text-center text-gray-400 dark:text-gray-500 mb-2 uppercase tracking-wide">
              Atual vs Ano Anterior
            </p>

            {/* Barras agrupadas */}
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={barData} barCategoryGap="35%" barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.4} />
                <XAxis dataKey="gender" tick={{ fontSize: 12, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={50}
                  tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)}
                />
                <Tooltip<number, string> content={BarCompareTooltip} />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  wrapperStyle={{ fontSize: 12, color: '#9ca3af' }}
                />
                <Bar dataKey="Atual"    fill={BAR_ATUAL}    radius={[4, 4, 0, 0]} name="Atual" />
                <Bar dataKey="Anterior" fill={BAR_ANTERIOR} radius={[4, 4, 0, 0]} name="Ano anterior" />
              </BarChart>
            </ResponsiveContainer>

            {/* Cards de delta */}
            <div className="mt-4 grid grid-cols-2 gap-3">
              <GenderYoYDelta
                label="Homens"
                color={GENDER_COLORS[0]}
                diff={maleDiff}
                pct={malePct}
                prev={previousData.male}
                cur={data.male}
              />
              <GenderYoYDelta
                label="Mulheres"
                color={GENDER_COLORS[1]}
                diff={femaleDiff}
                pct={femalePct}
                prev={previousData.female}
                cur={data.female}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
